import { useEffect, useState } from 'react'
import { DisplayTypePicker } from '../feeds/DisplayTypePicker'
import {
  Button,
  Dialog,
  ErrorSummary,
  Field,
  FormLayout,
  FormLayoutRow,
  HelpTip,
  Input,
  Select,
  Switch,
} from '../common'
import styles from './SurfaceCreateModal.module.css'

const NON_ALPHANUMERIC_RUN_PATTERN = /[^a-z0-9]+/g
const EDGE_HYPHEN_RUN_PATTERN = /^-+|-+$/g
const HYPHENATED_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

const HELP_TEXT = {
  displayType:
    "Controls how posts in this surface render. Pick from the eleven surface layouts; switch later in the inspector.",
  bettingMode:
    "Picks the bet-placement integration the widget loads. None = content-only. Kambi WAPI = loads the Kambi widget API. Custom = provider supplied by the host page.",
  showBettingChrome:
    "When on, posts on this surface show bet legs, odds, and a betslip CTA. Turn off for content-only surfaces.",
  includeByDefault:
    "New posts automatically target this surface unless the author un-checks it in the Publish-to picker.",
  enabled:
    "Disabled surfaces still exist for history but don't receive new posts and disappear from widget fetches.",
}

const BETTING_MODE_OPTIONS = [
  { value: 'none', label: 'None (content only)' },
  { value: 'wapi_kambi', label: 'Kambi WAPI' },
  { value: 'custom', label: 'Custom' },
]


const BLANK_FORM = {
  name: '',
  slug: '',
  displayType: 'story_block',
  bettingMode: 'none',
  showBettingChrome: false,
  includeByDefault: true,
  isEnabled: true,
  config: {},
}

function slugifyName(name) {
  return name
    .toLowerCase()
    .trim()
    .replace(NON_ALPHANUMERIC_RUN_PATTERN, '-')
    .replace(EDGE_HYPHEN_RUN_PATTERN, '')
    .slice(0, 64)
}

function fromSurface(surface) {
  return {
    name: surface.name,
    slug: surface.slug,
    displayType: surface.display_type,
    bettingMode: surface.betting_mode,
    showBettingChrome: surface.show_betting_chrome,
    includeByDefault: surface.include_by_default,
    isEnabled: surface.is_enabled,
    config: surface.config || {},
  }
}

function toPayload(form) {
  const config = { ...(form.config || {}) }

  return {
    name: form.name.trim(),
    slug: form.slug.trim(),
    display_type: form.displayType,
    betting_mode: form.bettingMode,
    show_betting_chrome: form.showBettingChrome,
    include_by_default: form.includeByDefault,
    is_enabled: form.isEnabled,
    config,
  }
}

/**
 * SurfaceCreateModal — create or edit a feed surface. Extracted from the old
 * FeedsPage so the Configuration workspace can reuse it directly. Parent owns
 * the open/close state and the mutation; this component only validates shape.
 */
export default function SurfaceCreateModal({
  open,
  mode = 'create', // 'create' | 'edit'
  initialSurface = null,
  saving = false,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState(BLANK_FORM)
  const [slugEdited, setSlugEdited] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && initialSurface) {
      setForm(fromSurface(initialSurface))
      setSlugEdited(true)
    } else {
      setForm(BLANK_FORM)
      setSlugEdited(false)
    }
    setError(null)
  }, [open, mode, initialSurface])

  const handleName = (event) => {
    const name = event.target.value
    setForm((prev) => ({
      ...prev,
      name,
      slug: slugEdited ? prev.slug : slugifyName(name),
    }))
  }

  const handleSlug = (event) => {
    setSlugEdited(true)
    setForm((prev) => ({ ...prev, slug: event.target.value }))
  }


  const handleSubmit = () => {
    const payload = toPayload(form)
    if (!payload.name) {
      setError('Name is required')
      return
    }
    if (!HYPHENATED_SLUG_PATTERN.test(payload.slug)) {
      setError('Slug must be lowercase letters, digits, and hyphens')
      return
    }
    setError(null)
    onSubmit(payload)
  }

  return (
    <Dialog
      open={open}
      title={mode === 'edit' ? 'Edit surface' : 'New surface'}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving || !form.name.trim()}>
            {saving ? 'Saving…' : mode === 'edit' ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <FormLayout errorSummary={error ? <ErrorSummary>{error}</ErrorSummary> : null}>
        <FormLayoutRow columns={2}>
          <Field label="Name">
            <Input value={form.name} onChange={handleName} data-autofocus required />
          </Field>
          <Field label="Slug" help="Lowercase letters, digits, and hyphens.">
            <Input value={form.slug} onChange={handleSlug} required />
          </Field>
        </FormLayoutRow>


        <div className={styles.pickerSection}>
          <div className={styles.pickerLabel}>
            Display type
            <HelpTip text={HELP_TEXT.displayType} placement="right" />
          </div>
          <DisplayTypePicker
            value={form.displayType}
            onChange={(next) => setForm((prev) => ({ ...prev, displayType: next }))}
          />
        </div>

        <Field
          label="Betting mode"
          labelExtra={<HelpTip text={HELP_TEXT.bettingMode} placement="right" />}
        >
          <Select
            value={form.bettingMode}
            onChange={(event) => setForm((prev) => ({ ...prev, bettingMode: event.target.value }))}
          >
            {BETTING_MODE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Select>
        </Field>

        <div className={styles.toggles}>
          <div className={styles.toggleRow}>
            <Switch
              label="Show betting controls on posts"
              checked={form.showBettingChrome}
              onChange={(checked) => setForm((prev) => ({ ...prev, showBettingChrome: checked }))}
            />
            <HelpTip text={HELP_TEXT.showBettingChrome} placement="top" />
          </div>
          <div className={styles.toggleRow}>
            <Switch
              label="Include by default for new posts"
              checked={form.includeByDefault}
              onChange={(checked) => setForm((prev) => ({ ...prev, includeByDefault: checked }))}
            />
            <HelpTip text={HELP_TEXT.includeByDefault} placement="top" />
          </div>
          <div className={styles.toggleRow}>
            <Switch
              label="Enabled"
              checked={form.isEnabled}
              onChange={(checked) => setForm((prev) => ({ ...prev, isEnabled: checked }))}
            />
            <HelpTip text={HELP_TEXT.enabled} placement="top" />
          </div>
        </div>
      </FormLayout>
    </Dialog>
  )
}
